import { useEffect, useRef, useCallback, useState } from 'react';
import socketService from '@/lib/socket';
import { GroupChatMessage, Message, MessageBlock, Choices } from '@/types';

interface UseGroupChatOptions {
  conversationId: string;
  isGroupChat: boolean;
  onSelfMessage?: (message: GroupChatMessage) => void;
  onUserMessage?: (message: GroupChatMessage) => void;
  onMessageInit?: (message: GroupChatMessage) => void;
  onMessageStart?: (message: GroupChatMessage) => void;
  onThink?: (message: GroupChatMessage) => void;
  onContent?: (message: GroupChatMessage) => void;
  onHashCode?: (message: GroupChatMessage) => void;
  onAnyMessage?: (message: GroupChatMessage) => void;
}

interface UseGroupChatReturn {
  isConnected: boolean;
  socketId: string | null;
}

/**
 * Hook for handling WebSocket messages in group chats
 * Listens to 'chat_message' events and dispatches to appropriate handlers
 */
export function useGroupChat({
  conversationId,
  isGroupChat,
  onSelfMessage,
  onUserMessage,
  onMessageInit,
  onMessageStart,
  onThink,
  onContent,
  onHashCode,
  onAnyMessage,
}: UseGroupChatOptions): UseGroupChatReturn {
  const [isConnected, setIsConnected] = useState(
    socketService.isSocketConnected()
  );
  const socketIdRef = useRef<string | null>(
    socketService.getSocketId() || null
  );

  // Track connection status
  useEffect(() => {
    const checkConnection = () => {
      const connected = socketService.isSocketConnected();
      setIsConnected(connected);
      if (connected) {
        socketIdRef.current = socketService.getSocketId() || null;
      }
    };

    // Check immediately
    checkConnection();

    // Set up interval to check connection status
    const interval = setInterval(checkConnection, 1000);

    return () => clearInterval(interval);
  }, []);

  // Handle incoming chat_message events
  const handleChatMessage = useCallback(
    (rawData: string | GroupChatMessage) => {
      // 🔥 解析 JSON 字符串：WebSocket 返回的是字符串，需要反序列化
      let data: GroupChatMessage;
      try {
        data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      } catch (e) {
        console.error('Failed to parse chat_message:', e, rawData);
        return;
      }
      // Only process messages for the current conversation
      // 🔥 注意：self_message 和 user_message 的 conversationId 可能是数字，其他消息是 UUID 字符串
      // 所以需要更灵活的比较方式
      if (
        data.conversationId &&
        data.type !== 'self_message' &&
        data.type !== 'user_message'
      ) {
        if (String(data.conversationId) !== String(conversationId)) {
          console.log('⚠️ Skipping message: conversationId mismatch');
          return;
        }
      }
      // 🔥 对于 user_message，也检查 conversationId，但如果匹配就处理
      if (data.type === 'user_message' && data.conversationId) {
        if (String(data.conversationId) !== String(conversationId)) {
          console.log('⚠️ Skipping user_message: conversationId mismatch', {
            dataConvId: data.conversationId,
            currentConvId: conversationId,
          });
          return;
        }
      }

      // Call the generic handler if provided
      if (onAnyMessage) {
        onAnyMessage(data);
      }

      // Dispatch to specific handlers based on message type
      switch (data.type) {
        case 'self_message':
          if (onSelfMessage) {
            onSelfMessage(data);
          }
          break;
        case 'user_message':
          if (onUserMessage) {
            onUserMessage(data);
          }
          break;
        case 'message_init':
          if (onMessageInit) {
            onMessageInit(data);
          }
          break;
        case 'message_start':
          if (onMessageStart) {
            onMessageStart(data);
          }
          break;
        case 'think':
          if (onThink) {
            onThink(data);
          }
          break;
        case 'content':
        case 'text': // 🔥 AI 回复的文本内容类型是 'text'
          if (onContent) {
            onContent(data);
          }
          break;
        case 'hash_code':
        case 'hash-code': // 🔥 后端返回的是 'hash-code'（连字符）
          if (onHashCode) {
            onHashCode(data);
          }
          break;
        default:
          console.log('Unknown group chat message type:', data.type);
      }
    },
    [
      conversationId,
      onSelfMessage,
      onUserMessage,
      onMessageInit,
      onMessageStart,
      onThink,
      onContent,
      onHashCode,
      onAnyMessage,
    ]
  );

  // 🔥 加入群聊房间
  useEffect(() => {
    if (!isConnected || !isGroupChat || !conversationId) {
      return;
    }

    // 发送 join_conversation_room 事件加入房间
    console.log('🔔 Joining conversation room:', conversationId);
    socketService.emit('join_conversation_room', conversationId);

    // 监听加入房间的响应（可选，用于错误处理）
    const handleJoinError = (error: any) => {
      console.error('❌ Failed to join conversation room:', error);
    };

    socketService.on('join_room_error', handleJoinError);

    return () => {
      socketService.off('join_room_error', handleJoinError);
      // 离开房间时可以发送 leave 事件（如果后端支持）
      // socketService.emit('leave_conversation_room', { conversation_id: conversationId });
    };
  }, [isConnected, isGroupChat, conversationId]);

  // Set up WebSocket listener for group chat messages
  // 🔥 恢复 isGroupChat 门控：只在群聊时监听 chat_message 事件
  // skill 会话有自己的 WS handler（useMessageOperations.ts 中的 socketService.on('chat_message', ...)）
  // 如果两个 handler 同时监听，会导致所有消息被处理两次，造成重复渲染
  useEffect(() => {
    if (!isConnected || !isGroupChat) {
      return;
    }

    // Listen for chat_message events
    socketService.on('chat_message', handleChatMessage);

    return () => {
      socketService.off('chat_message', handleChatMessage);
    };
  }, [isConnected, handleChatMessage]);

  return {
    isConnected,
    socketId: socketIdRef.current,
  };
}

/**
 * Helper function to convert GroupChatMessage to Message format
 * for displaying in the chat UI
 */
export function groupChatMessageToMessage(
  groupMsg: GroupChatMessage
): (Message & { user?: typeof groupMsg.user; isOwnMessage?: boolean }) | null {
  const now = new Date();

  switch (groupMsg.type) {
    case 'self_message':
    case 'user_message': {
      // User messages (from self or others)
      const isOwnMessage = groupMsg.type === 'self_message';
      const blocks: MessageBlock[] = [];

      // 🔥 添加文件块（图片等附件）
      if (groupMsg.fileIds && groupMsg.fileIds.length > 0) {
        groupMsg.fileIds.forEach(fileId => {
          blocks.push({
            type: 'file',
            content: String(fileId),
            id: String(fileId),
            fileId: String(fileId),
          });
        });
      }

      // 添加文本块
      if (groupMsg.content) {
        blocks.push({
          type: 'text',
          content: groupMsg.content,
        });
      }

      // 如果没有任何内容，至少添加空文本块
      if (blocks.length === 0) {
        blocks.push({
          type: 'text',
          content: '',
        });
      }

      const choice: Choices = {
        blocks,
        version: 1,
      };
      return {
        id: String(groupMsg.previousMessageId || Date.now()),
        backendId: groupMsg.previousMessageId,
        role: 'user',
        choices: [choice],
        timestamp: now,
        // Add user info for group chat display
        user: groupMsg.user,
        isOwnMessage,
        // 🔥 添加 client 信息，用于 @Agent 标签显示
        client: groupMsg.client
          ? {
              id: String(groupMsg.client.id),
              name: groupMsg.client.name,
              logo: groupMsg.client.logo,
            }
          : undefined,
      };
    }

    case 'message_init': {
      // AI response initialization - create placeholder
      const emptyChoice: Choices = {
        blocks: [],
        version: 1,
      };
      return {
        id: String(groupMsg.messageId || Date.now()),
        backendId: groupMsg.messageId,
        role: 'assistant',
        choices: [emptyChoice],
        timestamp: now,
        isStreaming: true,
      };
    }

    case 'content': {
      // AI content - should be appended to existing assistant message
      // Return null as this should update existing message, not create new
      return null;
    }

    case 'think': {
      // AI thinking - similar to content, should update existing message
      return null;
    }

    default:
      return null;
  }
}

/**
 * Helper function to update messages array with group chat content
 */
export function updateMessagesWithGroupChatContent(
  messages: Message[],
  groupMsg: GroupChatMessage
): Message[] {
  if (groupMsg.type === 'content' || groupMsg.type === 'think') {
    // Find the last assistant message and append content
    const updatedMessages = [...messages];
    for (let i = updatedMessages.length - 1; i >= 0; i--) {
      if (updatedMessages[i].role === 'assistant') {
        const message = updatedMessages[i];
        const activeChoiceIndex = message.activeChoice || 0;
        const currentChoice = message.choices[activeChoiceIndex];

        if (!currentChoice) {
          break;
        }

        const newContent = groupMsg.content || '';
        const blockType: MessageBlock['type'] =
          groupMsg.type === 'think' ? 'think' : 'text';

        // Find the last block of the same type to append to, or create new block
        const blocks = [...currentChoice.blocks];
        const lastBlock = blocks[blocks.length - 1];

        if (lastBlock && lastBlock.type === blockType) {
          // Append to existing block of the same type
          blocks[blocks.length - 1] = {
            ...lastBlock,
            content: lastBlock.content + newContent,
          };
        } else {
          // Create new block
          const newBlock: MessageBlock = {
            type: blockType,
            content: newContent,
          };
          blocks.push(newBlock);
        }

        const updatedChoice: Choices = {
          ...currentChoice,
          blocks,
        };

        const updatedChoices = [...message.choices];
        updatedChoices[activeChoiceIndex] = updatedChoice;

        updatedMessages[i] = {
          ...message,
          choices: updatedChoices,
        };
        break;
      }
    }
    return updatedMessages;
  }

  return messages;
}

export default useGroupChat;
